/**
 * 管理后台「经营分析」六页的接口与类型：经营驾驶舱、仪表盘、付费分析、成本测算、成本管控、用量统计。
 *
 * 背景：这六页对应 Alice 的 /admin/gateway/{business-cockpit,stats,analytics/payers,cost-analytics,cost-controls}
 *   与用量统计；本仓对应 gateway_control 的 /control/admin/...（routes_admin_biz.py），收入来自支付服务，
 *   由控制面在后端聚合，前端只调这一组接口。
 * 设计意图：
 *   - 不改共用的 admin.ts：这里自己发请求，路径逐字对应后端；遇到 401 先调 adminApi.me() 触发共用 http 层的
 *     会话刷新（同一时刻只刷新一次），成功后重试一次；失败时 me() 会通知应用回登录页。
 *   - 金额字段一律是 1e-8 美元的整数 unit（字段名带 _units），null 表示未知，不当作 0；花瓣与美元的换算由页面做。
 *   - 驾驶舱、付费分析的结论文案由前端按后端给的 key 取三语（后端只给 key 与数值），所以类型里 key 是字符串联合。
 * 关键约束：错误统一抛 ApiError；写接口 admin 角色，查询接口 support 即可（后端校验，前端只隐藏按钮）。
 */
import { adminApi } from './admin.ts'
import { ApiError, type Query } from './http.ts'

// ------------------------------------------------------------------ 仪表盘

export interface Period {
  start: string
  end: string
  days: number
  timezone: string
  today: string
}

export interface Pnl {
  revenue_units: number
  commercial_cost_units: number
  internal_cost_units: number
  net_units: number
  commercial_unknown_requests: number
  metered_requests: number
  metered_list_units: number
}

export interface OverviewStats {
  period: Period
  exclude_internal: boolean
  /** 支付服务取不到时的错误码；此时收入类字段为 0，页面要提示，不能当作「没有收入」。 */
  payments_error: string | null
  total_accounts: number
  total_models: number
  active_keys: number
  total_charge_units: number
  active_accounts: number
  requests: number
  charge_units: number
  recharge: { amount_units: number; order_count: number; paying_accounts: number }
  tokens: { input: number; output: number; cached: number; cache_write: number }
  metrics: {
    requests_per_account: number
    tokens_per_account: number
    charge_per_account_units: number
    arpu_units: number
    arppu_units: number
    paying_rate: number
  }
  pnl: Pnl
}

export interface TrendDay {
  date: string
  requests: number
  charge_units: number
  commercial_cost_units: number
  internal_cost_units: number
  input_tokens: number
  output_tokens: number
  cached_tokens: number
  revenue_units: number
}

export interface StackSummary {
  units: number
  line?: string
  model?: string
  source?: string
}

export interface RetentionRow {
  date: string
  dau: number
  new_accounts: number
  retention: Record<'d1' | 'd3' | 'd7' | 'd14' | 'd30' | 'd60', number | null>
}

export interface OverviewTrend {
  period: { start: string; end: string }
  payments_error: string | null
  daily: TrendDay[]
  line_summary: StackSummary[]
  line_daily: Record<string, Record<string, number>>
  model_summary: StackSummary[]
  model_daily: Record<string, Record<string, number>>
  scene_summary: StackSummary[]
  scene_daily: Record<string, Record<string, number>>
  dau_daily: RetentionRow[]
  top_accounts: { account_id: string; name: string; requests: number; charge_units: number }[]
  top_models: { model: string; requests: number; tokens: number; charge_units: number; upstream_units: number }[]
}

export interface StatsQuery {
  days?: number
  start_date?: string
  end_date?: string
  exclude_internal?: boolean
}

// ------------------------------------------------------------------ 用量统计

export interface ModelUsageOrg {
  account_id: string
  name: string
  is_internal: boolean
  requests: number
  input_tokens: number
  output_tokens: number
  cached_tokens: number
  total_tokens: number
  cost_units: number
  last_used_at: string | null
}

export interface ModelUsage {
  model_options: string[]
  summary: {
    requests: number
    accounts: number
    input_tokens: number
    output_tokens: number
    cached_tokens: number
    total_tokens: number
    cost_units: number
  }
  accounts: ModelUsageOrg[]
  total_accounts: number
  limit: number
  offset: number
  filters: { model_id: string; start: string; end: string; exclude_internal: boolean }
}

export interface ModelUsageQuery {
  model_id?: string
  start?: string
  end?: string
  exclude_internal?: boolean
  limit?: number
  offset?: number
}

// ------------------------------------------------------------------ 成本测算

export interface CostMetric {
  requests: number
  users: number
  known_cost_requests: number
  unknown_cost_requests: number
  cost_coverage: number
  user_charge_units: number
  known_user_charge_units: number
  known_upstream_cost_units: number
  known_margin_units: number
  known_margin_rate: number | null
  unknown_user_charge_units: number
  free_requests: number
  free_known_cost_units: number
  input_tokens: number
  output_tokens: number
  cached_tokens: number
  unknown_tokens: number
  metered_list_units: number
  avg_known_cost_per_user_units?: number
  avg_known_cost_per_1000_requests_units?: number
}

export type BusinessKey = 'overall' | 'conversation' | 'moments_subsidy' | 'other' | 'unattributed'
export type BusinessSplit = Record<BusinessKey, CostMetric>

export interface CostDaily {
  date: string
  external: CostMetric
  internal: CostMetric
  business: BusinessSplit
  business_attribution_rate: number
}

export interface CostUserRow extends CostMetric {
  account_id: string
  name: string
  active_days: number
  period_paid_units: number
  lifetime_paid_units: number
}

export interface CostAnalytics {
  start_date: string
  end_date: string
  timezone: string
  currency: string
  payments_error: string | null
  summary: { company_known_cost_units: number; external: CostMetric; internal: CostMetric }
  today_business: BusinessSplit & { date: string }
  daily: CostDaily[]
  models: (CostMetric & { provider_id: string; model_id: string })[]
  sources: (CostMetric & { source: string })[]
  top_users: CostUserRow[]
  unknown_risk_users: CostUserRow[]
}

export interface CostAnalyticsQuery {
  start_date?: string
  end_date?: string
  days?: number
}

// ------------------------------------------------------------------ 付费分析

export interface PayerAnalytics {
  generated_at: string
  timezone: string
  payments_error: string | null
  from_cache: boolean
  summary: {
    payers: number
    orders: number
    revenue_units: number
    repurchasers: number
    repurchaser_revenue_units: number
    repeat_orders: number
    avg_interval_days: number | null
    median_interval_days: number | null
  }
  matured: {
    matured14_users: number
    matured14_repurchasers: number
    matured14_repurchase_in14d: number
    matured21_users: number
    matured21_repurchasers: number
  }
  distribution: { times: string; users: number; orders: number; revenue_units: number }[]
  weekly_cohorts: { week_start: string; users: number; repurchased: number }[]
  monthly: { month: string; payers: number; returning_payers: number; revenue_units: number; returning_revenue_units: number }[]
  /** Alice 的支付入口与获客来源在本仓没有数据来源，恒为空数组，页面显示空状态。 */
  entry_channels: unknown[]
  acquisition_channels: unknown[]
  entry_channels_monthly: unknown[]
  usage_overview: { payer: PayerUsage; free: PayerUsage }
  model_preference: { model_id: string; kind: string; users: number; requests: number; tokens: number; cost_units: number }[]
  segments: { segment: string; users: number; pay_units: number; repurchasers: number; avg_cost_units: number }[]
  repurchaser_vs_single: {
    is_repurchaser: boolean
    users: number
    avg_pay_units: number
    avg_cost_units: number
    avg_m_tokens: number
    avg_images: number
    avg_active_days: number
  }[]
  top_payers: {
    account_id: string
    name: string
    pay_n: number
    pay_units: number
    cost_units: number
    m_tokens: number
    images: number
    active_days: number
    top_model: string
    first_paid: string
  }[]
}

export interface PayerUsage {
  users: number
  requests: number
  tokens: number
  image_requests: number
  cost_units: number
  image_cost_units: number
}

export interface AnalyticsReport {
  id: number
  title: string
  source: 'import' | 'agent'
  status: 'done' | 'running' | 'failed'
  model_id: string
  created_by: string
  created_at: string
  content?: string
}

// ------------------------------------------------------------------ 经营驾驶舱

export type CockpitStatus = 'observe' | 'broad' | 'targeted' | 'wait'
export type SegmentKey =
  | 'paid_urgent'
  | 'paid_soon'
  | 'trial_ready'
  | 'paid_healthy'
  | 'trial_nurture'
  | 'paid_stocked'
  | 'grant_stocked'
  | 'free_only'

export interface CockpitBucket {
  key: 'lt3' | 'd3_7' | 'd8_21' | 'd22_60' | 'gt60' | 'no_spend'
  users: number
  paid_users: number
  unpaid_users: number
  balance_units: number
}

export interface CockpitInventory {
  users: number
  balance_units: number
  spent_units_7d: number
  runway_days: number | null
}

export interface Cockpit {
  generated_at: string
  timezone: string
  payments_error: string | null
  decision: {
    status: CockpitStatus
    readiness_score: number
    paid_spenders: number
    urgent_payers: number
    near_window_payers: number
    urgent_share: number
    near_window_share: number
  }
  summary: {
    active_accounts_7d: number
    active_balance_units: number
    active_spent_units_7d: number
    active_balance_runway_days: number | null
    high_momentum_orgs: number
    free_only_orgs: number
  }
  window: {
    revenue_7d_units: number
    revenue_prev_7d_units: number
    revenue_growth: number | null
    spend_7d_units: number
    spend_prev_7d_units: number
    spend_growth: number | null
  }
  /** 充值活动在本仓由另一模块管理，驾驶舱暂不读取，status 恒为 unavailable。 */
  campaign: { mode: string | null; status: string; cooling: boolean; days_since_last_special: number | null }
  inventory: { paid: CockpitInventory; unpaid: CockpitInventory; buckets: CockpitBucket[] }
  donts: { key: string; n?: number }[]
  next_actions: { key: string; n?: number }[]
  triggers: { key: string; met: boolean; value: number | null }[]
  segments: {
    key: SegmentKey
    priority: number
    users: number
    balance_units: number
    spent_units_7d: number
    median_runway_days: number | null
  }[]
  gift_guidance: { key: string; users: number }[]
  priority_users: {
    account_id: string
    name: string
    segment: SegmentKey
    balance_units: number
    spent_units_7d: number
    spent_units_prev_7d: number
    requests_7d: number
    active_days_7d: number
    runway_days: number | null
    paid_orders: number
    lifetime_paid_units: number
    momentum: 'high' | 'medium' | 'low'
  }[]
  daily: {
    date: string
    active_accounts: number
    requests: number
    spent_units: number
    revenue_units: number
    payers: number
  }[]
}

// ------------------------------------------------------------------ 成本管控

export interface CostControls {
  providers: { id: string; name: string; is_active: boolean; cost_accounting: 'billed' | 'unbilled' }[]
  models: { id: string; display_name: string; kind: string; is_active: boolean; internal_redirect_to: string }[]
  cap_rules: CapRule[]
  thresholds: Thresholds
  watch: {
    date: string
    total_cost_units: number
    total_limit_units: number
    total_over: boolean
    accounts: { account_id: string; name: string; window: 'daily' | 'hourly'; cost_units: number; limit_units: number }[]
  }
}

export interface CapRule {
  name: string
  match: string
  cap_usd: number
}

export interface Thresholds {
  account_daily_usd: number
  total_daily_usd: number
  account_hourly_usd: number
}

// ------------------------------------------------------------------ 请求层

function failure(status: number, body: unknown): ApiError {
  const err = (body as { error?: { code?: string; message?: string } } | null)?.error
  return new ApiError(status, err?.code ?? `http_${status}`, err?.message ?? 'Request failed')
}

function url(path: string, query?: Query): string {
  if (!query) return path
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue
    qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `${path}?${s}` : path
}

async function request<T>(method: string, path: string, query?: Query, body?: unknown): Promise<T> {
  const target = url(path, query)
  for (let attempt = 0; attempt < 2; attempt++) {
    let resp: Response
    try {
      resp = await fetch(target, {
        method,
        credentials: 'same-origin',
        headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
      })
    } catch (e) {
      console.error('[api] 网络错误', method, target, e)
      throw new ApiError(0, 'network_error', 'Network error')
    }
    const text = await resp.text()
    let parsed: unknown
    try {
      parsed = text ? JSON.parse(text) : undefined
    } catch {
      parsed = undefined
    }
    if (resp.status === 401 && attempt === 0) {
      try {
        await adminApi.me()
      } catch (e) {
        console.warn('[api] 会话刷新失败，不再重试', e)
        throw failure(resp.status, parsed)
      }
      continue
    }
    if (!resp.ok) {
      const err = failure(resp.status, parsed)
      console.warn('[api] 请求失败', method, target, err.status, err.code)
      throw err
    }
    return parsed as T
  }
  throw new ApiError(401, 'not_logged_in', 'Login required.')
}

const C = '/control/admin'

export const bizApi = {
  stats: (q: StatsQuery = {}) => request<OverviewStats>('GET', `${C}/stats`, q as Query),
  trend: (q: StatsQuery = {}) => request<OverviewTrend>('GET', `${C}/stats/trend`, q as Query),
  modelUsage: (q: ModelUsageQuery = {}) => request<ModelUsage>('GET', `${C}/stats/model-usage`, q as Query),
  cockpit: () => request<Cockpit>('GET', `${C}/business-cockpit`),
  costAnalytics: (q: CostAnalyticsQuery = {}) => request<CostAnalytics>('GET', `${C}/cost-analytics`, q as Query),
  payers: (refresh = false) => request<PayerAnalytics>('GET', `${C}/analytics/payers`, { refresh: refresh ? 'true' : undefined }),
  reports: (limit = 30) => request<{ reports: AnalyticsReport[] }>('GET', `${C}/analytics/reports`, { limit }),
  report: (id: number) => request<AnalyticsReport>('GET', `${C}/analytics/reports/${id}`),
  importReport: (title: string, content: string) =>
    request<AnalyticsReport>('POST', `${C}/analytics/reports/import`, undefined, { title, content }),

  costControls: () => request<CostControls>('GET', `${C}/cost-controls`),
  setProviderAccounting: (id: string, cost_accounting: 'billed' | 'unbilled', reason = '') =>
    request<{ id: string; cost_accounting: string }>('PATCH', `${C}/cost-controls/providers/${encodeURIComponent(id)}`, undefined, {
      cost_accounting,
      reason,
    }),
  setRedirect: (modelId: string, internal_redirect_to: string, reason = '') =>
    request<{ id: string; internal_redirect_to: string }>('PATCH', `${C}/cost-controls/models/${modelId}`, undefined, {
      internal_redirect_to,
      reason,
    }),
  putCapRules: (rules: CapRule[], reason = '') =>
    request<{ cap_rules: CapRule[] }>('PUT', `${C}/cost-controls/cap-rules`, undefined, { rules, reason }),
  putThresholds: (values: Partial<Thresholds>, reason = '') =>
    request<{ thresholds: Thresholds }>('PUT', `${C}/cost-controls/thresholds`, undefined, { values, reason }),
}
